/**
 * Tier 2 Predictive Search — native Custom Element (no jQuery).
 * Opens via [data-ts-search-open]; fetches /search/suggest?section_id=predictive-search.
 */
class TsPredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.controller = null;
    this.debounceTimer = null;
    this.lastQuery = '';
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
  }

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.form = this.querySelector('form');
    this.input = this.querySelector('[data-ts-search-input]');
    this.panel = this.querySelector('[data-ts-search-panel]');
    this.resultsSlot = this.querySelector('[data-ts-search-results]');
    this.closeButtons = this.querySelectorAll('[data-ts-search-close]');

    if (!this.dialog || !this.input || !this.form) return;

    this.unavailableProducts =
      this.dataset.unavailableProducts || 'last';
    this.sectionId = this.dataset.sectionId || 'predictive-search';

    this.closeButtons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.close();
      });
    });

    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });

    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    });

    this.input.addEventListener(
      'input',
      this.debounce(() => this.onInput(), 300)
    );

    this.form.addEventListener('submit', (e) => {
      const q = this.input.value.trim();
      if (!q) {
        e.preventDefault();
      }
    });

    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
    this.abortFetch();
    clearTimeout(this.debounceTimer);
  }

  onDocumentClick(event) {
    const openTrigger = event.target.closest('[data-ts-search-open]');
    if (openTrigger) {
      event.preventDefault();
      this.open();
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.isOpen()) {
      event.preventDefault();
      this.close();
    }
  }

  isOpen() {
    return this.dialog?.open === true;
  }

  open() {
    if (!this.dialog || this.isOpen()) return;

    if (typeof this.dialog.showModal === 'function') {
      this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
    }

    document.body.classList.add('ts-predictive-search-open');
    this.input.setAttribute('aria-expanded', 'true');
    this.syncQueryState('');
    this.input.value = '';
    this.lastQuery = '';
    if (this.resultsSlot) this.resultsSlot.innerHTML = '';

    requestAnimationFrame(() => {
      this.input.focus();
    });
  }

  close() {
    if (!this.dialog || !this.isOpen()) return;

    this.abortFetch();
    clearTimeout(this.debounceTimer);

    if (typeof this.dialog.close === 'function') {
      this.dialog.close();
    } else {
      this.dialog.removeAttribute('open');
    }

    document.body.classList.remove('ts-predictive-search-open');
    this.input.setAttribute('aria-expanded', 'false');
    this.setLoading(false);
  }

  onInput() {
    const query = this.input.value.trim();
    this.syncQueryState(query);

    if (!query.length) {
      this.abortFetch();
      this.setLoading(false);
      if (this.resultsSlot) this.resultsSlot.innerHTML = '';
      this.lastQuery = '';
      return;
    }

    if (query === this.lastQuery) return;
    this.getSearchResults(query);
  }

  syncQueryState(query) {
    if (!this.panel) return;
    this.panel.dataset.hasQuery = query.length > 0 ? 'true' : 'false';
  }

  setLoading(isLoading) {
    if (!this.panel) return;
    this.panel.dataset.state = isLoading ? 'loading' : 'idle';
  }

  predictiveSupported() {
    try {
      return window.Shopify?.predictiveSearch !== false;
    } catch (_) {
      return true;
    }
  }

  getSearchResults(searchTerm) {
    if (!this.predictiveSupported()) {
      this.setLoading(false);
      return;
    }

    this.abortFetch();
    this.controller = new AbortController();
    this.setLoading(true);

    const root = window.Shopify?.routes?.root || '/';
    const params = new URLSearchParams();
    params.set('q', searchTerm);
    params.set('resources[type]', 'product');
    params.set('resources[limit]', '10');
    params.set(
      'resources[options][unavailable_products]',
      this.unavailableProducts
    );
    params.set('section_id', this.sectionId);

    const url = `${root}search/suggest?${params.toString()}`;

    fetch(url, { signal: this.controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const section = doc.querySelector(`#shopify-section-${this.sectionId}`);
        if (this.resultsSlot) {
          this.resultsSlot.innerHTML = section ? section.innerHTML : '';
        }
        this.lastQuery = searchTerm;
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        if (this.resultsSlot) this.resultsSlot.innerHTML = '';
      })
      .finally(() => {
        this.setLoading(false);
      });
  }

  abortFetch() {
    if (this.controller) {
      this.controller.abort();
      this.controller = null;
    }
  }

  debounce(fn, wait) {
    return (...args) => {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

if (!customElements.get('ts-predictive-search')) {
  customElements.define('ts-predictive-search', TsPredictiveSearch);
}
